import type {
  Domain,
  Facet,
  GraphRelationType,
  GraphSearchMode,
} from './model';

export const ROOT_NODE_ID = 'problem-root';

export interface EisenGraphNode {
  id: string;
  kind: 'problem' | 'domain' | 'facet';
  title: string;
  domain?: Domain;
  urgency?: number;
  importance?: number;
  summary?: string;
  aiAnalysis: string[];
  relationNotes: string[];
  connectionCount: number;
  searchText: string;
  x?: number;
  y?: number;
  vx?: number;
  vy?: number;
  fx?: number;
  fy?: number;
}

export interface EisenGraphEdge {
  id: string;
  source: string;
  target: string;
  type: GraphRelationType;
  inferred: boolean;
  note?: string;
}

export interface EisenGraphData {
  nodes: EisenGraphNode[];
  links: EisenGraphEdge[];
  adjacency: Map<string, Set<string>>;
}

export interface GraphSearchResult {
  nodeId: string;
  score: number | null;
  matchedFields: string[];
  node: EisenGraphNode;
}

interface BuildGraphOptions {
  includeDomainAnchors?: boolean;
}

const normalize = (value: string) => value.toLowerCase().replace(/\s+/g, ' ').trim();

const domainNodeId = (domain: Domain) => `domain:${domain}`;

const buildSearchText = (node: Omit<EisenGraphNode, 'searchText' | 'connectionCount'>) =>
  normalize(
    [
      node.title,
      node.domain ?? '',
      node.summary ?? '',
      ...node.aiAnalysis,
      ...node.relationNotes,
    ].join(' '),
  );

export function buildEisenGraph(
  problemTitle: string,
  facets: Facet[],
  options: BuildGraphOptions = {},
): EisenGraphData {
  const includeDomainAnchors = options.includeDomainAnchors ?? true;
  const nodes: EisenGraphNode[] = [];
  const links: EisenGraphEdge[] = [];
  const nodeMap = new Map<string, EisenGraphNode>();
  const edgeIds = new Set<string>();
  const connectionCounts = new Map<string, number>();

  const registerNode = (node: Omit<EisenGraphNode, 'connectionCount' | 'searchText'>) => {
    const nextNode: EisenGraphNode = {
      ...node,
      aiAnalysis: node.aiAnalysis,
      relationNotes: node.relationNotes,
      connectionCount: 0,
      searchText: buildSearchText(node),
    };
    nodes.push(nextNode);
    nodeMap.set(node.id, nextNode);
  };

  registerNode({
    id: ROOT_NODE_ID,
    kind: 'problem',
    title: problemTitle.trim() || 'Current Challenge',
    aiAnalysis: [],
    relationNotes: [],
  });

  if (includeDomainAnchors) {
    const domains = new Set<Domain>(facets.map((facet) => facet.domain));
    domains.forEach((domain) => {
      registerNode({
        id: domainNodeId(domain),
        kind: 'domain',
        title: domain,
        domain,
        aiAnalysis: [],
        relationNotes: [],
      });
    });
  }

  facets.forEach((facet) => {
    registerNode({
      id: facet.id,
      kind: 'facet',
      title: facet.title,
      domain: facet.domain,
      urgency: facet.urgency,
      importance: facet.importance,
      summary: facet.summary,
      aiAnalysis: facet.aiAnalysis,
      relationNotes: (facet.relations ?? [])
        .map((relation) => relation.note?.trim())
        .filter((note): note is string => Boolean(note)),
    });
  });

  const addEdge = (edge: EisenGraphEdge) => {
    if (edgeIds.has(edge.id)) return;
    edgeIds.add(edge.id);
    links.push(edge);
    connectionCounts.set(edge.source, (connectionCounts.get(edge.source) ?? 0) + 1);
    connectionCounts.set(edge.target, (connectionCounts.get(edge.target) ?? 0) + 1);
  };

  facets.forEach((facet) => {
    addEdge({
      id: `${ROOT_NODE_ID}->${facet.id}:decomposes_to`,
      source: ROOT_NODE_ID,
      target: facet.id,
      type: 'decomposes_to',
      inferred: true,
    });

    if (includeDomainAnchors) {
      addEdge({
        id: `${domainNodeId(facet.domain)}->${facet.id}:related`,
        source: domainNodeId(facet.domain),
        target: facet.id,
        type: 'related',
        inferred: true,
        note: `${facet.domain} anchor`,
      });
    }

    (facet.relations ?? []).forEach((relation) => {
      if (!nodeMap.has(relation.targetId)) return;
      addEdge({
        id: `${facet.id}->${relation.targetId}:${relation.type}`,
        source: facet.id,
        target: relation.targetId,
        type: relation.type,
        inferred: false,
        note: relation.note,
      });
    });
  });

  nodes.forEach((node) => {
    node.connectionCount = connectionCounts.get(node.id) ?? 0;
  });

  const adjacency = new Map<string, Set<string>>();
  links.forEach((link) => {
    if (!adjacency.has(link.source)) adjacency.set(link.source, new Set<string>());
    if (!adjacency.has(link.target)) adjacency.set(link.target, new Set<string>());
    adjacency.get(link.source)?.add(link.target);
    adjacency.get(link.target)?.add(link.source);
  });

  return { nodes, links, adjacency };
}

export function getNodeNeighborhood(
  adjacency: Map<string, Set<string>>,
  nodeId: string,
  depth: number,
): Set<string> {
  const visited = new Set<string>([nodeId]);
  const frontier: Array<{ id: string; depth: number }> = [{ id: nodeId, depth: 0 }];

  while (frontier.length > 0) {
    const current = frontier.shift();
    if (!current) break;
    if (current.depth >= depth) continue;
    const neighbors = adjacency.get(current.id);
    neighbors?.forEach((neighbor) => {
      if (visited.has(neighbor)) return;
      visited.add(neighbor);
      frontier.push({ id: neighbor, depth: current.depth + 1 });
    });
  }

  return visited;
}

export function searchGraphNodes(
  nodes: EisenGraphNode[],
  query: string,
  mode: GraphSearchMode,
): GraphSearchResult[] {
  const normalizedQuery = normalize(query);
  if (!normalizedQuery) return [];

  const tokens = normalizedQuery.split(' ').filter(Boolean);
  const effectiveMode = mode === 'semantic' ? 'auto' : mode;

  const scored = nodes
    .filter((node) => node.kind === 'facet')
    .map((node) => {
      const matchedFields = new Set<string>();
      let score = 0;

      const title = normalize(node.title);
      const summary = normalize(node.summary ?? '');
      const analysis = normalize(node.aiAnalysis.join(' '));
      const domain = normalize(node.domain ?? '');
      const relationNotes = normalize(node.relationNotes.join(' '));

      if (title.includes(normalizedQuery)) {
        matchedFields.add('title');
        score += title === normalizedQuery ? 140 : 110;
      }
      if (summary.includes(normalizedQuery)) {
        matchedFields.add('summary');
        score += 70;
      }
      if (analysis.includes(normalizedQuery)) {
        matchedFields.add('analysis');
        score += 55;
      }
      if (domain.includes(normalizedQuery)) {
        matchedFields.add('domain');
        score += 25;
      }
      if (relationNotes.includes(normalizedQuery)) {
        matchedFields.add('relations');
        score += 35;
      }

      tokens.forEach((token) => {
        if (token.length < 2) return;
        if (title.includes(token)) {
          matchedFields.add('title');
          score += 20;
        }
        if (summary.includes(token)) {
          matchedFields.add('summary');
          score += 12;
        }
        if (analysis.includes(token)) {
          matchedFields.add('analysis');
          score += 10;
        }
        if (domain.includes(token)) {
          matchedFields.add('domain');
          score += 6;
        }
        if (relationNotes.includes(token)) {
          matchedFields.add('relations');
          score += 8;
        }
      });

      if (effectiveMode === 'auto' && score > 0) {
        score += node.connectionCount;
      }

      return {
        nodeId: node.id,
        score: score > 0 ? score : null,
        matchedFields: Array.from(matchedFields),
        node,
      };
    })
    .filter((result) => result.score !== null)
    .sort((a, b) => (b.score ?? 0) - (a.score ?? 0));

  return scored.slice(0, 8);
}
